import { getCandidateIdFromCache } from 'src/components/Profile/data';
import { ProfileActions } from 'src/components/Profile/reducer';
import {
  PROFILE_VISIBILITY,
  CREATE_RESUME,
  PROFILE_VISIBILITY_V2,
} from 'src/components/Router';
import { updateRoute, resetRoutes } from 'src/components/Router/actionCreators';
import { UPDATE_ROUTE, UPDATE_PAGE } from 'src/components/Router/reducer';
import { READ, EDIT } from 'src/consts';
import type { AnalyticsActionOrigin } from 'src/modules/analytics/types';
import type { AppConfig } from 'src/modules/appConfig/types';
import { isFeatureActive } from 'src/modules/features';
import { getShareableProfileExperimentVariant } from 'src/modules/features/shareableProfileAbTest';
import { getUnifiedSiteStatus, parseUrl } from 'src/modules/utils';
import type { ReduxInjections, Action, Card, Mode, ApiClient } from 'src/types';

type RouteHandler = () => void;

interface RouteHandlerConfig {
  handlers: RouteHandler[];
  queuedEvent: boolean;
}

type RouteHandlerStore = Record<string, RouteHandlerConfig>;

const routeHandlers: RouteHandlerStore = {};

const getRouteHandlerConfig = (routeHandlerKey: string) => {
  if (!routeHandlers[routeHandlerKey]) {
    routeHandlers[routeHandlerKey] = {
      handlers: [],
      queuedEvent: false,
    };
  }

  return routeHandlers[routeHandlerKey];
};

const getRouteHandlerKey = (card: Card, desc: string) => `${card}-${desc}`;

const callHandlers = (routeHandlerKey: string) => {
  const routeHandlerConfig = getRouteHandlerConfig(routeHandlerKey);

  if (routeHandlerConfig.handlers.length === 0) {
    routeHandlerConfig.queuedEvent = true;
  } else {
    routeHandlerConfig.handlers.forEach((handler) => {
      handler();
    });
  }
};

export const registerRouteChangeHandler = (
  card: Card,
  desc: string,
  cb: RouteHandler,
) => {
  const routeHandlerKey = getRouteHandlerKey(card, desc);
  const routeHandlerConfig = getRouteHandlerConfig(routeHandlerKey);

  routeHandlerConfig.handlers.push(cb);

  if (routeHandlerConfig.queuedEvent) {
    routeHandlerConfig.queuedEvent = false;
    callHandlers(routeHandlerKey);
  }
};

export const createRouteMiddleware =
  ({ api, appConfig }: { api: ApiClient; appConfig: AppConfig }) =>
  ({ getState, dispatch }: ReduxInjections) =>
  (next: (action: Action) => void) =>
  (
    action: Action & {
      payload: { to: Card; mode: Mode; params: { focusOn: string } };
    },
  ) => {
    const { type: actionName } = action;

    if (actionName === UPDATE_ROUTE) {
      const {
        payload: {
          to: card,

          params: { focusOn },
        },
      } = action;

      if (focusOn) {
        const key = getRouteHandlerKey(card, focusOn);
        callHandlers(key);
      }
    }

    // resetRoutes should be dispatched before UPDATE_PAGE
    if (actionName === UPDATE_PAGE) {
      window.scrollTo(0, 0);
      dispatch(resetRoutes());
    }

    // updateRoute should be dispatched after PROFILE_RENDERED
    // Setting the returnAction here ensures that updateRoute
    // is dispatched after PROFILE_RENDERED
    const returnAction = next(action);

    // The following condition is in place for deep linking (https://www.seek.com.au/profile/me/profile-visibility)
    // To be revisited after shareable profile has gone full scale
    if (actionName === ProfileActions.PROFILE_RENDERED) {
      const {
        card,
        field,
        item,
        mode = EDIT,
      } = getState().router.queryStringRoute;

      const page = getState().router.page;
      const { zone, site } = appConfig;
      const candidateId = getCandidateIdFromCache(api.apolloClient.cache);

      const isShareableProfileExperimentActive =
        getShareableProfileExperimentVariant(zone, page, candidateId) === '1';

      const isShareableProfileFeatureFlagActive =
        isFeatureActive('shareable-profile');

      const isUnifiedSite = getUnifiedSiteStatus(site);

      // not using useIsShareableProfileFeatureActive here because we cannot use React hook outside of React functional component or hook
      const isShareableProfileFeatureActive =
        isShareableProfileExperimentActive ||
        isShareableProfileFeatureFlagActive ||
        isUnifiedSite;

      if (card && card !== CREATE_RESUME) {
        const isProfileVisibilityV2 =
          isShareableProfileFeatureActive && card === PROFILE_VISIBILITY;

        const params = {
          ...(field ? { focusOn: field } : {}),
          ...(item ? { id: item } : {}),
        };

        const defaultToReadMode =
          card === PROFILE_VISIBILITY && !isProfileVisibilityV2;

        const { profileDataSource = '' } =
          typeof window !== 'undefined' ? parseUrl(window.location) : {};

        const getCard = (): Card =>
          isProfileVisibilityV2
            ? (PROFILE_VISIBILITY_V2 as Card)
            : (card as Card);

        dispatch(
          updateRoute({
            to: getCard(),
            mode: defaultToReadMode ? READ : mode,
            params,
            analytics: {
              origin: profileDataSource as AnalyticsActionOrigin,
              currentPageSectionState: params.id ? 'edit' : 'add',
            },
          }),
        );
      }
    }

    return returnAction;
  };
